/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import EmberObject, { computed, action } from '@ember/object';
import { isPresent } from '@ember/utils';
import { task } from 'ember-concurrency';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import { ComponentLike } from '@glint/template';
import { AdNetworkType, TYPES } from 'cio/utils/ad-audiences';
import AudienceCredentialsService from 'cio/services/audience-credentials';
import AnalyticsService from 'cio/services/analytics';
import SegmentModel from 'cio/models/segment';
import AudienceCredentialModel from 'cio/models/audience-credential';
import AudienceModel from 'cio/models/audience';
import SegmentsCreateAudienceNetworksAdwords from 'cio/components/segments/create-audience/networks/adwords';
import SegmentsCreateAudienceNetworksFacebook from 'cio/components/segments/create-audience/networks/facebook';

export class Facebook extends EmberObject {
  type = TYPES.facebook;
  declare segment: SegmentModel;
  adAccountId: string | null = null;
  credential?: AudienceCredentialModel;

  @computed('credential', 'adAccountId')
  get isValid() {
    return isPresent(this.credential) && isPresent(this.adAccountId);
  }

  @computed('segment')
  get isDisabled() {
    if (!this.credential) {
      return false;
    }

    let audiences = (this.segment.audiences.content as AudienceModel[]).filterBy('type', TYPES.facebook) ?? [];
    return audiences.length >= (this.credential?.facebook?.adAccounts?.length ?? 0);
  }

  @computed('credential.isExpired')
  get isExpired() {
    return this.credential?.expired;
  }

  @computed('segment')
  get availableAdAccounts() {
    let allAccounts = this.credential?.facebook.adAccounts;

    if (!allAccounts) {
      return [];
    }

    let existingAccounts = (this.segment.audiences.content as AudienceModel[])
      .filterBy('type', TYPES.facebook)
      .map((a) => a.meta.audience?.accountId);

    return [
      { value: null, disabled: true, label: 'Select an ad account…' },
      ...allAccounts.map((account) => {
        return {
          value: `act_${account.accountId}`,
          label: account.name,
          disabled: existingAccounts.includes(account.accountId),
        };
      }),
    ];
  }
}

export class Adwords extends EmberObject {
  type = TYPES.adwords;
  declare segment: SegmentModel;
  adAccountId: string | null = null;
  credential?: AudienceCredentialModel;

  @computed('credential', 'adAccountId')
  get isValid() {
    return isPresent(this.credential) && isPresent(this.adAccountId);
  }

  @computed('segment')
  get isDisabled() {
    if (!this.credential) {
      return false;
    }

    let audiences = (this.segment.audiences.content as AudienceModel[]).filterBy('type', TYPES.adwords) ?? [];
    return audiences.length >= (this.credential?.adwords?.adAccounts?.length ?? 0);
  }

  @computed('credential.isExpired')
  get isExpired() {
    return this.credential?.expired;
  }

  @computed('segment')
  get availableCustomerAccounts() {
    let allAccounts = this.credential?.adwords.adAccounts;

    if (!allAccounts) {
      return [];
    }

    let existingAccounts = (this.segment.audiences.content as AudienceModel[])
      .filterBy('type', TYPES.adwords)
      .map((a) => a.meta.audience?.accountId);

    return [
      { value: null, disabled: true, label: 'Select an account…' },
      ...allAccounts.map((account) => {
        return {
          value: account.accountId,
          label: account.nameWithId,
          disabled: existingAccounts.includes(account.accountId),
        };
      }),
    ];
  }
}

interface SelectNetworkArgs {
  segment: SegmentModel;
  adAccountId: string | null;
  onConnect: (event: Event) => unknown;
  selectedNetwork: Facebook | Adwords;
  onSelect(network: Facebook | Adwords): void;
}

export default class SelectNetwork extends Component<SelectNetworkArgs> {
  @service store!: DS.Store;
  @service audienceCredentials!: AudienceCredentialsService;
  @service analytics!: AnalyticsService;

  readonly TYPES = TYPES;
  readonly networkComponent: Record<AdNetworkType, ComponentLike<any>> = {
    adwords: SegmentsCreateAudienceNetworksAdwords,
    facebook: SegmentsCreateAudienceNetworksFacebook,
  };

  @tracked networks: [Adwords, Facebook] | null = null;

  @action
  setupNetworks() {
    let credentials = this.store.peekAll('audience-credential');

    this.networks = [
      Adwords.create({
        segment: this.args.segment,
        adAccountId: this.args.adAccountId,
        credential: credentials.findBy('type', TYPES.adwords),
      }),
      Facebook.create({
        segment: this.args.segment,
        adAccountId: this.args.adAccountId,
        credential: credentials.findBy('type', TYPES.facebook),
      }),
    ];
  }

  @action
  updateNetworks() {
    let { adAccountId, selectedNetwork: { type } = {} } = this.args;
    this.networks?.forEach((network) => {
      (network.set as any)('adAccountId', network.type === type ? adAccountId : null);
    });
  }

  connectTask = task(async (network: Adwords | Facebook) => {
    this.analytics.track('audience', 'add_network_started', {
      type: network.type,
      startingPoint: 'Segment',
    });

    let credential = await this.audienceCredentials.setupProviderTask.perform(network.type);
    (network.set as any)('credential', credential);
  });
}
